/**
 * usePersona - the single source of truth for the UI-visibility predicates
 * derived from the authenticated user's RELATIONSHIPS (practice memberships),
 * not the global account role (which collapsed to {platform_admin, member};
 * ADR 0001 Addendum A). The one legitimately global distinction, platform
 * scope, is still read from the account role.
 *
 * Primary nav, the dashboard cards, and in-screen action links all read these
 * predicates from here, so a server-gated surface cannot be hidden in the nav
 * yet offered elsewhere. Beta 2026-07-22: the nav gated Enquiries on front-office
 * authority while the dashboard card linked there unconditionally, so a tutor
 * (support_worker) was offered a surface the server denies.
 *
 * The server independently enforces access on every route - this only governs
 * what the chrome offers.
 */
import { practiceSurfaceAccess } from './practice-access.ts';
import type { PracticeMembership } from '../api/me.ts';
import { useMe } from './use-me.ts';

export interface Persona {
  /** NovaLattix platform operator - runs the SaaS, holds no practice membership. */
  isPlatformAdmin: boolean;
  /** Holds only client_contact relationship(s) and no staff membership. */
  isClientOnly: boolean;
  /** Has at least one DPA-accepted (active) practice membership. */
  hasActivePractice: boolean;
  /** ADR 0004 - front-office bundle (enquiries). Owners hold it implicitly. */
  hasFrontOfficeAuthority: boolean;
  /** ADR 0004 - back-office bundle (contracts, templates, billing identity). */
  hasBackOfficeAuthority: boolean;
  /** Per-practice `hasBackOfficeAuthority` - for an entity action button, so the check can't borrow authority from a different membership. */
  hasBackOfficeAuthorityFor: (practiceId: string) => boolean;
  /** Per-practice `hasFrontOfficeAuthority`. The cross-practice predicate above answers
   *  "may they reach /enquiries at all"; a PER-PRACTICE card must not offer the pipeline
   *  on the strength of a grant held somewhere else. */
  hasFrontOfficeAuthorityFor: (practiceId: string) => boolean;
  /** Some practice makes them a professional-record keeper. Credentials are user-scoped
   *  (`/registrations` takes no practice); CPD is not - ask `useActiveCapability` there. */
  hasOwnRecordMembership: boolean;
  /** Practices where they may add a client, as an existing client or an enquiry. */
  clientAddingPractices: readonly PracticeMembership[];
  /** Step-up door on a case they cannot read, so there is no membership to scope to. */
  canRequestClinicalAccessAnywhere: boolean;
  /** Some membership holds `case.manage_billing` — same shape as the door above. */
  canManageBillingAnywhere: boolean;
  /** The server refuses a non-`support_worker` recipient; else the screen cannot fill. */
  canHoldWorkingAgreement: boolean;
}

const hasCapability = (m: PracticeMembership, capability: string): boolean =>
  (m.capabilities ?? []).includes(capability);

/** True when ANY membership confers the capability - never a gate for a screen that acts on
 *  the ACTIVE practice; that question is `useActiveCapability` in active-practice.tsx. */
const holds = (memberships: readonly PracticeMembership[], capability: string): boolean =>
  memberships.some((m) => hasCapability(m, capability));

/**
 * Pure derivation, so the predicates can be reasoned about without React.
 *
 * The authority predicates now read the SERVER's resolved capability set rather than
 * re-deriving from `roleInPractice`. Each role comparison removed here was a copy of a
 * server rule, free to drift from it — the class of bug that put a tutor in front of a
 * screen the server denies.
 *
 * Some predicates deliberately still read the role. `isClientOnly` describes the shape of
 * a person's RELATIONSHIPS, not what they may do. `canHoldWorkingAgreement` reads the role
 * because only a tutor was ever issued a working agreement to keep a copy of.
 */
export function derivePersona(role: string, memberships: readonly PracticeMembership[]): Persona {
  const isPlatformAdmin = role === 'platform_admin';
  // not-authorization: persona shell (allowed case 3) — which UI a person sees.
  const hasStaffMembership = memberships.some((m) => m.roleInPractice !== 'client_contact');
  return {
    isPlatformAdmin,
    isClientOnly:
      !isPlatformAdmin &&
      !hasStaffMembership &&
      // not-authorization: persona shell.
      memberships.some((m) => m.roleInPractice === 'client_contact'),
    hasActivePractice: memberships.some((m) => m.dpaAccepted),
    hasFrontOfficeAuthority: holds(memberships, 'practice.front_office'),
    hasBackOfficeAuthority: holds(memberships, 'practice.back_office'),
    hasBackOfficeAuthorityFor: (practiceId) =>
      memberships.some((m) => m.practiceId === practiceId && hasCapability(m, 'practice.back_office')),
    hasFrontOfficeAuthorityFor: (practiceId) =>
      memberships.some((m) => m.practiceId === practiceId && hasCapability(m, 'practice.front_office')),
    hasOwnRecordMembership: holds(memberships, 'practice.own_record'),
    // not-authorization: persona shell — which doors to offer.
    clientAddingPractices: memberships.filter((m) => {
      const a = practiceSurfaceAccess(m);
      return a.canQuickAdd || a.canSeeEnquiries;
    }),
    canRequestClinicalAccessAnywhere: holds(memberships, 'case.request_clinical_access'),
    canManageBillingAnywhere: holds(memberships, 'case.manage_billing'),
    // not-authorization: persona shell — shape-of-relationships, like `isClientOnly`.
    canHoldWorkingAgreement: memberships.some((m) => m.roleInPractice === 'support_worker'),
  };
}

export function usePersona(): Persona {
  const me = useMe();
  return derivePersona(me.data?.role ?? '', me.data?.memberships ?? []);
}

// The policy-gate rule lives in `policy-gate.ts` — pure, React-free and unit-tested there;
// `ProtectedLayout` imports it directly. Nothing is re-exported from here, and there is no
// `usePolicyGate` hook: the shell is the only consumer and it needs `now` explicitly. The
// `useDpaGate` this replaced had no consumer either, so a second route to the rule would be
// dead weight that can drift from the one thing that uses it.
